import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrderById, type Order } from '../../services/order.api';
import {
  ArrowLeft,
  Package,
  Truck,
  Calendar,
  MapPin,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  ExternalLink,
  Wrench,
  FileText,
  ChevronRight,
  Home,
} from 'lucide-react';
import { Button } from '../../components/ui';
import { cn } from '../../lib/utils';
import { ROUTES, getBookingDetailRoute } from '../../config/routes';
import { TimelineProgress, TechnicianProfile, SmartBadge } from '../../components/orders-bookings';

const statusConfig = {
  placed: { 
    icon: Clock, 
    label: 'Placed', 
    color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900' 
  },
  processing: { 
    icon: Package, 
    label: 'Processing', 
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/20 dark:text-yellow-400 dark:border-yellow-900' 
  },
  shipped: { 
    icon: Truck, 
    label: 'Shipped', 
    color: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/20 dark:text-purple-400 dark:border-purple-900' 
  },
  out_for_delivery: { 
    icon: Truck, 
    label: 'Out for Delivery', 
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/20 dark:text-indigo-400 dark:border-indigo-900' 
  },
  delivered: { 
    icon: CheckCircle2, 
    label: 'Delivered', 
    color: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900' 
  },
  completed: { 
    icon: CheckCircle2, 
    label: 'Completed', 
    color: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-900' 
  },
  canceled: { 
    icon: AlertCircle, 
    label: 'Canceled', 
    color: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900' 
  },
};

type Tab = 'status' | 'booking' | 'shipping';

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const { t } = useTranslation('orders');
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('status');

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getOrderById(orderId!);
      setOrder(data);
    } catch (err: unknown) {
      console.error('Failed to load order:', err);
      setError(err instanceof Error ? err.message : 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">{t('detail.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex items-center justify-center min-h-screen px-6 bg-background">
        <div className="text-center space-y-4 max-w-md">
          <div className="h-16 w-16 rounded-xl bg-red-50 dark:bg-red-950/20 flex items-center justify-center mx-auto border border-red-200 dark:border-red-900">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold">{t('detail.notFound')}</h2>
          <p className="text-sm text-muted-foreground">{error || t('detail.couldNotLoad')}</p>
          <Button onClick={() => navigate(ROUTES.ORDERS)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('detail.backToOrders')}
          </Button>
        </div>
      </div>
    );
  }

  const status = statusConfig[order.status as keyof typeof statusConfig] || statusConfig.placed;
  const StatusIcon = status.icon;
  const orderDate = new Date(order.createdAt);
  const expectedDate = order.estimatedDelivery ? new Date(order.estimatedDelivery) : null;

  const getTimelineSteps = () => {
    const steps = [
      { 
        label: 'Order Placed', 
        completed: false, 
        date: orderDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        description: 'Your order has been confirmed'
      },
      { 
        label: 'Processing', 
        completed: false,
        description: 'Preparing your item for shipment'
      },
      { 
        label: 'Shipped', 
        completed: false,
        description: 'Package is on its way'
      },
      { 
        label: 'Out for Delivery', 
        completed: false,
        description: 'Package is out for delivery'
      },
      { 
        label: 'Delivered', 
        completed: false,
        date: expectedDate ? expectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : undefined,
        description: 'Package delivered'
      },
    ];

    const statusIndex: { [key: string]: number } = {
      placed: 0,
      processing: 1,
      shipped: 2,
      out_for_delivery: 3,
      delivered: 4,
      completed: 4,
      installed: 4,
    };

    const currentIndex = statusIndex[order.status] ?? 0;

    return steps.map((step, index) => ({
      ...step,
      completed: index < currentIndex,
      current: index === currentIndex,
    }));
  };

  const tabs: { id: Tab; label: string; icon: React.ComponentType<{ className?: string }>; badge?: string }[] = [
    { id: 'status', label: 'Order Status', icon: Package },
    { id: 'booking', label: 'Service Booking', icon: Wrench, badge: order.relatedBookingId ? '1' : undefined },
    { id: 'shipping', label: t('details.shippingAndTracking'), icon: Truck },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <section className="bg-gradient-to-b from-background to-muted/20 border-b">
        <div className="mx-auto max-w-5xl px-6 lg:px-8 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate(ROUTES.ORDERS)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('detail.backToOrders')}
          </Button>

          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="h-20 w-20 rounded-xl bg-muted flex items-center justify-center border flex-shrink-0 overflow-hidden">
                <Package className="h-10 w-10 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border",
                    status.color
                  )}>
                    <StatusIcon className="h-3.5 w-3.5" />
                    {status.label}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">
                    Order #{order.orderId}
                  </span>
                </div>
                <h1 className="text-3xl font-light mb-2 truncate">{order.productName}</h1>
                {order.productBrand && (
                  <p className="text-muted-foreground">
                    {order.productBrand} {order.productModel}
                  </p>
                )}
                {order.asset && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-2">
                    <Home className="h-3.5 w-3.5" />
                    For: {order.asset.name}
                  </p>
                )}
              </div>
            </div>

            <div className="text-right flex-shrink-0">
              <div className="text-3xl font-semibold">
                ${order.totalAmount.toFixed(2)}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {orderDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-2 border-b overflow-x-auto">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors relative",
                    isActive
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
                  )}
                >
                  <TabIcon className="h-4 w-4" />
                  {tab.label}
                  {tab.badge && (
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="mx-auto max-w-5xl px-6 lg:px-8 py-8">
        {/* Status Tab */}
        {activeTab === 'status' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            {/* Timeline */}
            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold mb-6 text-lg">{t('detail.deliveryProgress')}</h3>
              <TimelineProgress steps={getTimelineSteps()} />
            </div>

            {/* Diagnosis Issue */}
            {order.diagnosisIssue && (
              <div className="rounded-xl border bg-card p-6">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-50 dark:bg-amber-950/20 flex items-center justify-center flex-shrink-0 border border-amber-200 dark:border-amber-900">
                    <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-2">{t('detail.whyOrdered')}</h3>
                    <p className="text-muted-foreground">{order.diagnosisIssue}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Order Summary */}
            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-muted-foreground" />
                Order Summary
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('details.product')}</span>
                  <span className="font-medium text-right">{order.productName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('details.subtotal')}</span>
                  <span className="font-medium">${order.subtotal.toFixed(2)}</span>
                </div>
                {order.taxAmount && order.taxAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span className="font-medium">${order.taxAmount.toFixed(2)}</span>
                  </div>
                )}
                {order.shippingCost && order.shippingCost > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('details.shipping')}</span>
                    <span className="font-medium">${order.shippingCost.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t pt-3 flex justify-between">
                  <span className="font-semibold text-lg">Total</span>
                  <span className="font-semibold text-2xl">${order.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Booking Tab */}
        {activeTab === 'booking' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            {order.relatedBookingId ? (
              <>
                <div className="rounded-xl border bg-card p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-semibold text-lg">{t('detail.technicianService')}</h3>
                    <SmartBadge variant="arrives_before_service" size="small" />
                  </div>

                  {/* Mock technician data - in real app this would come from booking API */}
                  <TechnicianProfile
                    variant="card"
                    name="John Martinez"
                    rating={4.8}
                    reviewCount={127}
                    phone="+1 (555) 123-4567"
                    email="john.m@homeiq.com"
                    showContactButtons={true}
                  />
                </div>

                <div className="rounded-xl border bg-card p-6">
                  <h3 className="font-semibold mb-4">{t('detail.serviceDetails')}</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <div className="text-sm text-muted-foreground">{t('detail.scheduledDate')}</div>
                        <div className="font-medium">
                          {expectedDate ? expectedDate.toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          }) : t('detail.toBeScheduled')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <div className="text-sm text-muted-foreground">{t('detail.serviceLocation')}</div>
                        <div className="font-medium">{t('detail.yourProperty')}</div>
                      </div>
                    </div>
                  </div>

                  <Button 
                    className="w-full mt-6"
                    onClick={() => navigate(getBookingDetailRoute(order.relatedBookingId!))}
                  >
                    {t('detail.viewFullBooking')}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>

                <div className="rounded-xl border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 p-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-900 dark:text-amber-200 mb-1">{t('detail.partWillArriveBefore')}</h4>
                      <p className="text-sm text-amber-800 dark:text-amber-300">
                        {t('detail.partWillArriveBeforeBody')}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="rounded-xl border bg-card p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-muted mb-4">
                  <Wrench className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium mb-2">{t('detail.noServiceBooking')}</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                  {t('detail.noServiceBookingDesc')}
                </p>
                <Button variant="outline">
                  {t('detail.scheduleService')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Shipping Tab */}
        {activeTab === 'shipping' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            {/* Tracking Information */}
            {order.trackingNumber ? (
              <div className="rounded-xl border bg-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Truck className="h-5 w-5 text-muted-foreground" />
                  <h3 className="font-semibold">{t('detail.packageTracking')}</h3>
                </div>
                <div className="p-4 rounded-lg bg-muted/30 border mb-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="text-xs text-muted-foreground mb-1">{t('detail.trackingNumber')}</div>
                      <div className="font-mono font-medium text-lg mb-2">{order.trackingNumber}</div>
                      {order.carrier && (
                        <div className="text-sm text-muted-foreground">
                          {t('detail.carrier', { name: order.carrier })}
                        </div>
                      )}
                    </div>
                    <Button size="sm" variant="outline">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t('detail.track')}
                    </Button>
                  </div>
                </div>

                {expectedDate && (
                  <div className="flex items-center justify-between p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <div>
                        <div className="text-sm font-medium text-green-900 dark:text-green-200">{t('detail.expectedDelivery')}</div>
                        <div className="text-xs text-green-700 dark:text-green-300">
                          {expectedDate.toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                      {expectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl border bg-card p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-muted mb-4">
                  <Package className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium mb-2">{t('detail.trackingNotAvailable')}</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {t('detail.trackingNotAvailableDesc')}
                </p>
              </div>
            )}

            {/* Shipping Address */}
            <div className="rounded-xl border bg-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-semibold">{t('detail.deliveryAddress')}</h3>
              </div>
              <div className="p-4 rounded-lg bg-muted/30 border">
                <div className="font-medium mb-1">{t('detail.yourProperty')}</div>
                {order.asset && (
                  <p className="text-sm text-muted-foreground">{order.asset.name}</p>
                )}
              </div>
            </div>

            {/* Delivery Instructions */}
            <div className="rounded-xl border bg-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-semibold">{t('detail.deliveryInstructions')}</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('detail.deliveryInstructionsBody')}
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
